import {
  ClearableBy,
  ErrorCode,
  Severity,
  type Fault,
} from '@sb-protogen/external/external_control';

import {
  buildFault,
  isOperatorActionable,
  mapTcFault,
  mapTcRejection,
} from '@sb/external-control-signals';
import { TCErrorCode } from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';

export const GENERIC_TC_OPERATOR_MESSAGE = 'Motion Planning Error.';

/**
 * Operator-facing failureReason/Error text for a TC fault: "Motion Planning
 * Error: <detail>" when `isOperatorActionable`, otherwise the bare generic
 * message.
 */
export function operatorFaultMessage(
  fault: Pick<Fault, 'code' | 'message'>,
): string {
  return isOperatorActionable(fault.code)
    ? `Motion Planning Error: ${fault.message}`
    : GENERIC_TC_OPERATOR_MESSAGE;
}

/**
 * Is this a code the generated enum knows?
 *
 * The wire decoder passes an unrecognized enum int straight through
 * (`message.error = reader.int32() as any`), and `tcErrorCode()` is exhaustive
 * with a `never` check, so it *throws* on one. A controller build ahead of this
 * enum must not take down the very handler that reports the failure — on the
 * event path that throw is swallowed by the messenger fan-out and the jog never
 * resolves; on the enqueue path it surfaces as `Unmapped TCErrorCode: N`, the
 * raw jargon these helpers exist to remove. So anything the enum does not name
 * is treated the same as no code at all.
 */
function isKnownTcErrorCode(
  error: TCErrorCode | undefined,
): error is TCErrorCode {
  return error !== undefined && TCErrorCode[error] !== undefined;
}

/**
 * Fault for a rejected TC enqueue reply — the request was refused and nothing
 * moved. A missing or unknown `error` gets the TC_CONTROLLER_ERROR fallback.
 */
export function tcRejectionFault(error: TCErrorCode | undefined): Fault {
  return isKnownTcErrorCode(error)
    ? mapTcRejection(error)
    : buildFault(
        ErrorCode.TC_CONTROLLER_ERROR,
        Severity.REJECTED,
        ClearableBy.CLEARABLE_BY_CLIENT,
      );
}

/**
 * Fault for a PRIMITIVE_FAILED event — the controller entered its error state,
 * so motion has already stopped. `error` is optional on the wire but
 * `enterError()` always fills it; a missing or unknown one means a failure with
 * no code we can name, which is TC_CONTROLLER_ERROR rather than TC_ERROR_NONE
 * ("no error").
 */
export function tcPrimitiveFault(error: TCErrorCode | undefined): Fault {
  return isKnownTcErrorCode(error)
    ? mapTcFault(error)
    : buildFault(
        ErrorCode.TC_CONTROLLER_ERROR,
        Severity.FAULTED,
        ClearableBy.CLEARABLE_BY_CLIENT,
      );
}
